/**
 * Verification Item Editor Dialog
 *
 * Dialog for creating and editing verification items.
 */

import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  Typography,
  FormGroup,
  FormControlLabel,
  Checkbox,
  Divider,
  ToggleButton,
  ToggleButtonGroup,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
} from '@mui/material';
import { Link as LinkIcon } from '@mui/icons-material';
import {
  VerificationItem,
} from '../../types/verification';
import {
  classificationKeys,
  methodKeys,
  getLocalizedClassificationLabel,
  getLocalizedMethodLabel,
} from '../../utils/verificationLabels';

interface VerificationItemEditorProps {
  open: boolean;
  item?: VerificationItem | null;
  unitId: string;
  onSave: (data: ItemFormData) => void;
  onClose: () => void;
}

export interface ItemFormData {
  specification: string;
  itemName: string;
  classifications: string[];
  standardTarget: string;
  verificationMethodsPrimary: string[];
  verificationMethodsSecondary: string[];
  qualityRecordNo: string;
  qualityRecordTitle: string;
  reviewJudgment: string | null;
  qualityRecordNoRight: string;
  qualityRecordTitleRight: string;
}

const VerificationItemEditor: React.FC<VerificationItemEditorProps> = ({
  open,
  item,
  unitId,
  onSave,
  onClose,
}) => {
  const { t } = useTranslation();
  const isEditing = !!item;
  // unitId is received from parent for context but not used directly in the editor form
  void unitId;

  const [form, setForm] = useState<ItemFormData>({
    specification: '',
    itemName: '',
    classifications: [],
    standardTarget: '',
    verificationMethodsPrimary: [],
    verificationMethodsSecondary: [],
    qualityRecordNo: '',
    qualityRecordTitle: '',
    reviewJudgment: null,
    qualityRecordNoRight: '',
    qualityRecordTitleRight: '',
  });

  useEffect(() => {
    if (item) {
      setForm({
        specification: item.specification,
        itemName: item.itemName,
        classifications: [...item.classifications],
        standardTarget: item.standardTarget,
        verificationMethodsPrimary: [...item.verificationMethodsPrimary],
        verificationMethodsSecondary: [...item.verificationMethodsSecondary],
        qualityRecordNo: item.qualityRecordNo,
        qualityRecordTitle: item.qualityRecordTitle,
        reviewJudgment: item.reviewJudgment,
        qualityRecordNoRight: item.qualityRecordNoRight,
        qualityRecordTitleRight: item.qualityRecordTitleRight,
      });
    } else {
      setForm({
        specification: '',
        itemName: '',
        classifications: [],
        standardTarget: '',
        verificationMethodsPrimary: [],
        verificationMethodsSecondary: [],
        qualityRecordNo: '',
        qualityRecordTitle: '',
        reviewJudgment: null,
        qualityRecordNoRight: '',
        qualityRecordTitleRight: '',
      });
    }
  }, [item, open]);

  const handleClassificationToggle = (key: string) => {
    setForm((prev) => ({
      ...prev,
      classifications: prev.classifications.includes(key)
        ? prev.classifications.filter((c) => c !== key)
        : [...prev.classifications, key],
    }));
  };

  const handleMethodChange = (key: string, type: 'primary' | 'secondary' | 'none') => {
    setForm((prev) => {
      const primary = prev.verificationMethodsPrimary.filter((m) => m !== key);
      const secondary = prev.verificationMethodsSecondary.filter((m) => m !== key);
      if (type === 'primary') primary.push(key);
      if (type === 'secondary') secondary.push(key);
      return {
        ...prev,
        verificationMethodsPrimary: primary,
        verificationMethodsSecondary: secondary,
      };
    });
  };

  const getMethodType = (key: string): string => {
    if (form.verificationMethodsPrimary.includes(key)) return 'primary';
    if (form.verificationMethodsSecondary.includes(key)) return 'secondary';
    return 'none';
  };

  const handleSubmit = () => {
    onSave(form);
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        {isEditing ? t('verification.editItem') : t('verification.addItem')}
      </DialogTitle>
      <DialogContent dividers>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
          {/* Basic fields */}
          <TextField
            label={t('verification.table.specification')}
            value={form.specification}
            onChange={(e) => setForm({ ...form, specification: e.target.value })}
            fullWidth
            size="small"
          />
          <TextField
            label={t('verification.table.itemName')}
            value={form.itemName}
            onChange={(e) => setForm({ ...form, itemName: e.target.value })}
            fullWidth
            size="small"
            required
          />

          {/* Classifications */}
          <Box>
            <Typography variant="subtitle2" gutterBottom>
              {t('verification.table.classification')} (◎)
            </Typography>
            <FormGroup row>
              {classificationKeys.map((key) => (
                <FormControlLabel
                  key={key}
                  control={
                    <Checkbox
                      checked={form.classifications.includes(key)}
                      onChange={() => handleClassificationToggle(key)}
                      size="small"
                    />
                  }
                  label={getLocalizedClassificationLabel(t, key)}
                  sx={{ mr: 2 }}
                />
              ))}
            </FormGroup>
          </Box>

          <Divider />

          {/* Standard/target */}
          <TextField
            label={t('verification.table.standardTarget')}
            value={form.standardTarget}
            onChange={(e) => setForm({ ...form, standardTarget: e.target.value })}
            fullWidth
            multiline
            rows={3}
            size="small"
            helperText={t('verification.standardTargetHelp')}
          />

          <Divider />

          {/* Verification methods */}
          <Box>
            <Typography variant="subtitle2" gutterBottom>
              {t('verification.table.verificationMethod')} (■{t('verification.primary')} / □{t('verification.secondary')})
            </Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5 }}>
              {methodKeys.map((key) => (
                <Box key={key} sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', minWidth: 80 }}>
                  <Typography variant="caption" sx={{ mb: 0.5, fontSize: '0.7rem' }}>
                    {getLocalizedMethodLabel(t, key)}
                  </Typography>
                  <ToggleButtonGroup
                    value={getMethodType(key)}
                    exclusive
                    onChange={(_, value) => {
                      if (value !== null) handleMethodChange(key, value);
                    }}
                    size="small"
                  >
                    <ToggleButton value="none" sx={{ px: 1, py: 0.25, fontSize: '0.65rem' }}>
                      -
                    </ToggleButton>
                    <ToggleButton value="primary" sx={{ px: 1, py: 0.25, fontSize: '0.65rem' }}>
                      ■
                    </ToggleButton>
                    <ToggleButton value="secondary" sx={{ px: 1, py: 0.25, fontSize: '0.65rem' }}>
                      □
                    </ToggleButton>
                  </ToggleButtonGroup>
                </Box>
              ))}
            </Box>
          </Box>

          <Divider />

          {/* Quality records & review */}
          <Box sx={{ display: 'flex', gap: 2 }}>
            <TextField
              label={t('verification.table.qualityRecordNo')}
              value={form.qualityRecordNo}
              onChange={(e) => setForm({ ...form, qualityRecordNo: e.target.value })}
              size="small"
              sx={{ flex: 1 }}
            />
            <TextField
              label={t('verification.table.qualityRecordTitle')}
              value={form.qualityRecordTitle}
              onChange={(e) => setForm({ ...form, qualityRecordTitle: e.target.value })}
              size="small"
              sx={{ flex: 1 }}
            />
          </Box>

          <FormControl size="small" sx={{ maxWidth: 200 }}>
            <InputLabel>{t('verification.table.review')}</InputLabel>
            <Select
              value={form.reviewJudgment || ''}
              onChange={(e) => setForm({ ...form, reviewJudgment: e.target.value || null })}
              label={t('verification.table.review')}
            >
              <MenuItem value="">-</MenuItem>
              <MenuItem value="required">{t('verification.reviewRequired')}</MenuItem>
              <MenuItem value="not_required">{t('verification.reviewNotRequired')}</MenuItem>
            </Select>
          </FormControl>

          <Box sx={{ display: 'flex', gap: 2 }}>
            <TextField
              label={`${t('verification.table.qualityRecordNo')} (${t('verification.right')})`}
              value={form.qualityRecordNoRight}
              onChange={(e) => setForm({ ...form, qualityRecordNoRight: e.target.value })}
              size="small"
              sx={{ flex: 1 }}
            />
            <TextField
              label={`${t('verification.table.qualityRecordTitle')} (${t('verification.right')})`}
              value={form.qualityRecordTitleRight}
              onChange={(e) => setForm({ ...form, qualityRecordTitleRight: e.target.value })}
              size="small"
              sx={{ flex: 1 }}
            />
          </Box>
        </Box>

        {/* DRBFM Traceability Info */}
        {item?.sourceDrbfmSessionId && (
          <Alert
            severity="info"
            icon={<LinkIcon />}
            sx={{ mt: 2 }}
            action={
              <Button
                size="small"
                onClick={() => window.open(`/drbfm?session=${item.sourceDrbfmSessionId}`, '_blank')}
              >
                {t('verification.drbfmLinkTooltip', 'DRBFM分析結果を表示')}
              </Button>
            }
          >
            {t('verification.drbfmLink', 'この項目はDRBFM分析から生成されました')}
          </Alert>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>{t('common.cancel')}</Button>
        <Button variant="contained" onClick={handleSubmit}>
          {t('common.save')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default VerificationItemEditor;
